import type { AppLocale } from '@batiflow/i18n';
import { useEffect, useLayoutEffect, useSyncExternalStore, type ReactNode } from 'react';
import { I18nextProvider } from 'react-i18next';
import { loadArabicFonts } from '@/shared/fonts';
import { directionOf } from '../locale-store';
import { useRuntime } from './runtime-provider';

/** The active UI language (from /me, else the saved choice, else French). */
export function useLocale(): AppLocale {
  const { localeStore } = useRuntime();
  return useSyncExternalStore(localeStore.subscribe, localeStore.getLocale, localeStore.getLocale);
}

/** Text direction of the active language: `rtl` for Arabic. */
export function useDirection(): 'ltr' | 'rtl' {
  return directionOf(useLocale());
}

/**
 * i18next (react-i18next) for the whole app. Also keeps `<html lang dir>` in step with the
 * language and loads the Arabic font only when Arabic is active.
 */
export function I18nProvider({ children }: { children?: ReactNode }) {
  const { i18n } = useRuntime();
  const locale = useLocale();
  const dir = directionOf(locale);

  useLayoutEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = dir;
  }, [locale, dir]);

  useEffect(() => {
    if (locale === 'ar') void loadArabicFonts();
  }, [locale]);

  return <I18nextProvider i18n={i18n}>{children}</I18nextProvider>;
}
